// The 'gamesetup' mode document's type and the camera-forcing rule it drives,
// shared between the editor (editor-panels.tsx, where it is authored) and the
// play surface (screens-play.tsx, where a published game reads it back) - a
// dependency-free module so the play bundle never pulls in the editor's own
// heavy imports (ObjectThumbnail, useMapEditorStore, etc.) just for this.

/** The 'gamesetup' mode document: every Game Setup field held as real state. */
export type GameSetupDoc = {
  type: string;
  projection: string;
  teams: string;
  match: boolean;
  skill: boolean;
  sides: boolean;
  region?: string;
  sessionLength?: string;
  winsNeeded?: string;
  winCondition?: string;
  respawnMode?: string;
  spawnProtection?: string;
  gravity?: string;
  /**
   * Which HollowScript capabilities (`entities`/`variables`/`audio`/`logic`)
   * this project's scripts may declare via `uses` - task item 4's
   * default-deny-per-project grant model
   * (docs/editor-consolidation/hollowscript-capabilities-v1.md §4).
   * `undefined` means "not yet configured": every v1 capability is granted,
   * matching tier 1's own posture for a creator's own project. An empty
   * array is a real, deliberate "no scripting capabilities at all", not the
   * same as undefined. Held as plain strings, not `StudioCapabilityName`,
   * so this dependency-free module (see the file doc comment - the play
   * bundle must not pull in the scripting module just for this field's
   * type) stays exactly that; a reader narrows against the real capability
   * names itself (see `PreviewRoute` in screens-editor.tsx).
   */
  scriptCapabilities?: readonly string[];
  /**
   * The project roster entry (`CharacterDefinition.id`) chosen as the
   * player's own body, in place of the stock prototype rig - authored by
   * `GameRulesPanel`'s "Player character" picker (editor-panels.tsx),
   * resolved at runtime by `GameRuntime.tsx`'s `resolvePlayerCharacter`.
   * `undefined` (the default, and every project authored before this field
   * existed) keeps the stock rig - a purely additive field.
   */
  playerCharacterId?: string;
};

export const GAMESETUP_DOC_FALLBACK: GameSetupDoc = {
  type: 'Versus',
  projection: '3D first person',
  teams: '5v5',
  match: true,
  skill: true,
  sides: true,
  region: 'EU West',
  sessionLength: '100s',
  winsNeeded: '7',
  winCondition: 'Objective complete',
  respawnMode: 'Per round',
  spawnProtection: '3s',
  gravity: '9.8',
};

/**
 * Camera portion of a Game Setup projection choice, or `null` for a 2D
 * choice (no first/third-person camera to force in that case).
 */
export function cameraModeFromProjection(projection: string): 'first' | 'third' | null {
  if (projection === '3D first person') return 'first';
  if (projection === '3D third person') return 'third';
  return null;
}

/**
 * `Tilemap2D.mode` implied by a Game Setup projection choice, or `undefined`
 * for a 3D choice (no tilemap movement model to set). Lets the Camera &
 * projection picker's 2D options and Pixel Studio's own mode toggle write
 * the same underlying field.
 */
export function tilemapModeFromProjection(projection: string): 'top-down' | 'platformer' | undefined {
  if (projection === '2D top down') return 'top-down';
  if (projection === '2D side view') return 'platformer';
  return undefined;
}
